/*
 * Line-level diff for the App Config compare view (088).
 *
 * The classic longest-common-subsequence table. Config values are prompts and short settings --
 * a few hundred lines at the very most -- so the O(n*m) table is the right size of solution, and
 * it buys the property a naive line-by-line walk cannot give: the result is a minimal edit
 * script, so inserting one line at the top reports one addition rather than reporting every
 * following line as changed.
 *
 * Returns rows in reading order:
 *   {type: 'same' | 'add' | 'remove', text, beforeLine, afterLine}
 * beforeLine/afterLine are 1-based line numbers, null on the side where the line is absent.
 */

// beyond this a diff stops being readable anyway, and the table stops being cheap
const MAX_LINES = 4000

const toLines = (text) => (text == null ? '' : String(text)).split('\n')

const diffLines = (before, after) => {
	const a = toLines(before)
	const b = toLines(after)

	if (a.length > MAX_LINES || b.length > MAX_LINES) {
		return [
			{type: 'remove', text: a.join('\n'), beforeLine: 1, afterLine: null},
			{type: 'add', text: b.join('\n'), beforeLine: null, afterLine: 1},
		]
	}

	// lengths[i][j] = length of the LCS of a[i..] and b[j..], built from the end backwards so
	// the walk below can go forwards and emit rows in reading order
	const lengths = Array.from({length: a.length + 1}, () => new Uint32Array(b.length + 1))
	for (let i = a.length - 1; i >= 0; i--) {
		for (let j = b.length - 1; j >= 0; j--) {
			lengths[i][j] = a[i] === b[j]
				? lengths[i + 1][j + 1] + 1
				: Math.max(lengths[i + 1][j], lengths[i][j + 1])
		}
	}

	const rows = []
	let i = 0
	let j = 0
	while (i < a.length && j < b.length) {
		if (a[i] === b[j]) {
			rows.push({type: 'same', text: a[i], beforeLine: i + 1, afterLine: j + 1})
			i++
			j++
		}
		// tie goes to the removal, so a replaced line reads as "was / now" rather than the
		// other way round
		else if (lengths[i + 1][j] >= lengths[i][j + 1]) {
			rows.push({type: 'remove', text: a[i], beforeLine: i + 1, afterLine: null})
			i++
		}
		else {
			rows.push({type: 'add', text: b[j], beforeLine: null, afterLine: j + 1})
			j++
		}
	}
	while (i < a.length) {
		rows.push({type: 'remove', text: a[i], beforeLine: i + 1, afterLine: null})
		i++
	}
	while (j < b.length) {
		rows.push({type: 'add', text: b[j], beforeLine: null, afterLine: j + 1})
		j++
	}

	return rows
}

/*
 * Pairs the linear diff into side-by-side rows: {left, right, type}, where left is the "before"
 * line and right the "after", either of which may be null when a line exists on one side only.
 *
 * A run of removals immediately followed by a run of additions is the same edit seen from both
 * sides, so those are zipped into 'change' rows and land on the same line of the viewer. An
 * uneven run simply leaves the shorter side blank rather than inventing an alignment.
 */
const alignRows = (rows) => {
	const paired = []
	let i = 0

	while (i < rows.length) {
		if (rows[i].type === 'same') {
			paired.push({type: 'same', left: rows[i], right: rows[i]})
			i++
			continue
		}

		const removes = []
		const adds = []
		while (i < rows.length && rows[i].type === 'remove') removes.push(rows[i++])
		while (i < rows.length && rows[i].type === 'add') adds.push(rows[i++])

		for (let k = 0; k < Math.max(removes.length, adds.length); k++) {
			const left = removes[k] || null
			const right = adds[k] || null
			paired.push({
				type: left && right ? 'change' : (left ? 'remove' : 'add'),
				left,
				right,
			})
		}
	}

	return paired
}

const diffStats = (rows) => ({
	added: rows.filter(r => r.type === 'add').length,
	removed: rows.filter(r => r.type === 'remove').length,
})

export {diffLines, alignRows, diffStats}
